import { resolveSchema, resolveDomainEvent } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import {
  buildSchemaField,
  resolveEntityRef,
  resolveFieldRefs,
  resolveFieldUpdateRefs,
  applyNestedFieldUpdates,
  normalizeDescription,
  buildSchemaRef
} from './schemaFields.js'
import { sanitizeCardDescription } from './sanitize.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType, MAX_DESCRIPTION_LENGTH, MAX_FIELD_DESCRIPTION_LENGTH } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

const unionQueryFields = (...fieldArrays) => {
  const byName = new Map()

  for (const fields of fieldArrays) {
    for (const field of fields || []) {
      const existing = byName.get(field.name)
      if (!existing) {
        byName.set(field.name, {
          ...field,
          fields: field.fields ? [...field.fields] : undefined
        })
        continue
      }
      if (field.fields?.length && existing.fields?.length) {
        existing.fields = unionQueryFields(existing.fields, field.fields)
      } else if (field.fields?.length) {
        existing.fields = unionQueryFields(field.fields)
      }
      if (field.type === 'filter' && existing.type !== 'filter') {
        existing.type = 'filter'
      }
      if (field.cardinality && !existing.cardinality) {
        existing.cardinality = field.cardinality
      }
      if (field.relatedEntityId && !existing.relatedEntityId) {
        existing.relatedEntityId = field.relatedEntityId
      }
    }
  }

  return Array.from(byName.values()).map((field) => {
    const cleaned = { ...field }
    if (!cleaned.fields) {
      delete cleaned.fields
    }

    return cleaned
  })
}

const findAllReadModelCardsBySchemaId = (domainEvents, schemaId, cardTypesMap) => {
  const matches = []

  for (let eventIndex = 0; eventIndex < domainEvents.length; eventIndex++) {
    const event = domainEvents[eventIndex]
    const cards = event.cards || []
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      const card = cards[cardIndex]
      const cardType = cardTypesMap[card.cardTypeId]
      if (cardType?.domainModelRole === DomainModelRole.READ_MODEL && card.schemaId === schemaId) {
        matches.push({ card, event, eventIndex, cardIndex })
      }
    }
  }

  return matches
}

export const buildCreateReadModelsMutation = ({ workflow, workflowId, readModels, cardTypes }) => {
  if (!Array.isArray(readModels) || readModels.length === 0) {
    return { error: 'readModels is required and must be a non-empty array' }
  }

  const cardTypesList = cardTypes || []
  const cardTypesMap = Object.fromEntries(cardTypesList.map((cardType) => [cardType.id, cardType]))
  const readModelCardType = cardTypesList.find((cardType) => cardType.domainModelRole === DomainModelRole.READ_MODEL)
  if (!readModelCardType) {
    return { error: 'Read model card type not found in this workflow' }
  }

  // nameToState: per read-model name, either a new schema being built or an existing schema being merged into.
  const nameToState = new Map()
  // batchEventNamePairs dedupes (eventId, name) so the same read model isn't attached twice to one event in a batch.
  const batchEventNamePairs = new Set()
  const pendingCards = []

  const domainEvents = workflow.domainEvents || []
  let lastSortkey = getLastCardSortkey(domainEvents)

  for (const entry of readModels) {
    const { domainEvent, name, description, entity, cardinality, fields } = entry || {}

    if (!domainEvent) {
      return { error: 'Each read model must have a domainEvent ref' }
    }
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return { error: 'Each read model must have a non-empty name' }
    }
    if (!cardinality) {
      return { error: `Each read model must have a cardinality (read model "${name}")` }
    }

    const normalizedDescription = normalizeDescription(description, MAX_DESCRIPTION_LENGTH)
    if (normalizedDescription.error) {
      return { error: `${normalizedDescription.error} (read model "${name}")` }
    }

    const matchedEvent = resolveDomainEvent(domainEvent, workflow)
    if (!matchedEvent) {
      return { error: `Domain event not found for ref: ${domainEvent}` }
    }

    const sanitizedName = sanitizeCardDescription(name)
    const pairKey = `${matchedEvent.id}::${sanitizedName}`
    if (batchEventNamePairs.has(pairKey)) {
      return { error: `Read model "${sanitizedName}" is targeted at event "${matchedEvent.description}" more than once in the batch` }
    }
    batchEventNamePairs.add(pairKey)

    const resolvedEntityId = entity ? resolveEntityRef(entity, workflow) : undefined
    const resolvedFields = resolveFieldRefs(fields, workflow)
    const incomingFields = (resolvedFields || []).map((field) => buildSchemaField(field, SchemaType.QUERY))

    let state = nameToState.get(sanitizedName)
    if (!state) {
      const existingSchema = (workflow.schemas || []).find((schema) => schema.name === sanitizedName && schema.type === SchemaType.QUERY)
      if (existingSchema) {
        state = {
          mode: 'existing',
          name: sanitizedName,
          schemaId: existingSchema.id,
          schemaIndex: workflow.schemas.findIndex((schema) => schema.id === existingSchema.id),
          existingVersion: existingSchema.version || null,
          existingFields: existingSchema.fields || [],
          existingCardinality: existingSchema.cardinality,
          mergedFields: existingSchema.fields || [],
          cardinality: existingSchema.cardinality,
          entityId: existingSchema.entityId,
          description: existingSchema.description
        }
      } else {
        state = {
          mode: 'new',
          name: sanitizedName,
          schemaId: generateId(),
          mergedFields: [],
          cardinality,
          entityId: resolvedEntityId,
          description: normalizedDescription.value
        }
      }
      nameToState.set(sanitizedName, state)
    }

    if (state.mode === 'existing') {
      const alreadyLinked = (matchedEvent.cards || []).some((card) => {
        return cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.READ_MODEL &&
          card.schemaId === state.schemaId
      })
      if (alreadyLinked) {
        return { error: `Read model "${sanitizedName}" is already attached to event "${matchedEvent.description}"` }
      }
    }

    state.mergedFields = unionQueryFields(state.mergedFields, incomingFields)
    if (cardinality) {
      state.cardinality = cardinality
    }
    if (resolvedEntityId && !state.entityId) {
      state.entityId = resolvedEntityId
    }
    if (normalizedDescription.value && !state.description) {
      state.description = normalizedDescription.value
    }

    const sortkey = computeMidString(lastSortkey, '')
    lastSortkey = sortkey

    pendingCards.push({
      eventId: matchedEvent.id,
      description: sanitizedName,
      schemaId: state.schemaId,
      sortkey
    })
  }

  const schemas = []
  for (const state of nameToState.values()) {
    if (state.mode === 'new') {
      schemas.push({
        id: state.schemaId,
        name: state.name,
        description: state.description,
        type: SchemaType.QUERY,
        cardinality: state.cardinality,
        entityId: state.entityId,
        fields: state.mergedFields,
        index: -1
      })
    } else {
      const fieldsChanged = JSON.stringify(state.mergedFields) !== JSON.stringify(state.existingFields)
      const cardinalityChanged = state.cardinality !== state.existingCardinality
      if (fieldsChanged || cardinalityChanged) {
        schemas.push({
          id: state.schemaId,
          name: state.name,
          description: state.description,
          type: SchemaType.QUERY,
          cardinality: state.cardinality,
          entityId: state.entityId,
          fields: state.mergedFields,
          index: state.schemaIndex,
          expectedVersion: state.existingVersion
        })
      }
    }
  }

  // schemaInput is null when every targeted read model already exists with no field/cardinality change —
  // only the card links are added in that case.
  const schemaInput = schemas.length > 0
    ? {
        id: workflowId,
        expectedVersion: workflow.version,
        schemas,
        updateNote: {
          action: 'created read models',
          target: schemas.map((schema) => schema.name).join(', ')
        }
      }
    : null

  const cards = pendingCards.map(({ eventId, description, schemaId, sortkey }) => {
    const eventIndex = domainEvents.findIndex((event) => event.id === eventId)

    return {
      description,
      done: false,
      cardTypeId: readModelCardType.id,
      releaseNo: null,
      sortkey,
      boundedContext: null,
      schemaId,
      cardIndex: -1,
      eventId,
      eventIndex
    }
  })

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists (if any).
  const cardInput = {
    workflowId,
    updateNote: {
      action: 'added read model cards',
      target: [...nameToState.keys()].join(', ')
    },
    cards
  }

  return {
    schemaInput,
    cardInput,
    refs: [...nameToState.values()].map((state) => ({ name: state.name, ref: buildSchemaRef('queries', state.name) }))
  }
}

export const buildUpdateReadModelsMutation = ({ workflow, workflowId, readModels, cardTypes }) => {
  if (!Array.isArray(readModels) || readModels.length === 0) {
    return { error: 'readModels is required and must be a non-empty array' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const claimedSchemaIds = new Set()
  const claimedNewNames = new Set()
  const schemaInputs = []
  const changeSummaries = []
  const refsOut = []
  const pendingRenames = []

  for (const update of readModels) {
    const {
      readModel: readModelRef,
      name,
      description,
      cardinality,
      entity,
      addFields: rawAdds,
      updateFields: rawUpdates,
      removeFields
    } = update || {}

    if (!readModelRef) {
      return { error: 'Each item must include a readModel ref' }
    }

    const resolved = resolveSchema(readModelRef, workflow, cardTypesMap)
    if (!resolved) {
      return { error: `Read model not found for ref: ${readModelRef}` }
    }
    const schemaId = resolved.id

    if (claimedSchemaIds.has(schemaId)) {
      return { error: `Read model "${readModelRef}" appears more than once in the batch` }
    }
    claimedSchemaIds.add(schemaId)

    const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
    if (schemaIndex === -1) {
      return { error: `Read model not found for ref: ${readModelRef}` }
    }

    const resolvedEntityId = entity ? resolveEntityRef(entity, workflow) : undefined
    const addFields = resolveFieldRefs(rawAdds, workflow)
    const updateFields = resolveFieldUpdateRefs(rawUpdates, workflow)

    const schema = workflow.schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in read model "${schema.name}"` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in read model "${schema.name}"` }
        }

        if (updates.name && updates.name !== fieldName) {
          const collision = currentFields.find((field) => field.name === updates.name)
          if (collision) {
            return { error: `Field "${updates.name}" already exists in read model "${schema.name}"` }
          }
        }

        const updatedField = { ...currentFields[fieldIndex] }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.description !== undefined) {
          const normalized = normalizeDescription(updates.description, MAX_FIELD_DESCRIPTION_LENGTH)
          if (normalized.error) {
            return { error: normalized.error }
          }
          if (normalized.value) {
            updatedField.description = normalized.value
          } else {
            delete updatedField.description
          }
        }
        if (updates.isFilter !== undefined) {
          if (updates.isFilter) {
            updatedField.type = 'filter'
          } else {
            delete updatedField.type
          }
        }
        if (updates.computed !== undefined) {
          if (updates.computed) {
            updatedField.computed = true
          } else {
            delete updatedField.computed
          }
        }
        if (updates.relatedEntityId !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntityId
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.fields !== undefined) {
          const nested = applyNestedFieldUpdates(updatedField.fields, updates.fields)
          if (nested && nested.length > 0) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        if (currentFields.find((existing) => existing.name === field.name)) {
          return { error: `Field "${field.name}" already exists in read model "${schema.name}"` }
        }
        currentFields.push(buildSchemaField(field, SchemaType.QUERY))
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    const sanitizedName = name !== undefined ? sanitizeCardDescription(name) : undefined
    if (sanitizedName !== undefined && sanitizedName !== schema.name) {
      const collision = workflow.schemas.find((other) => other.name === sanitizedName && other.type === SchemaType.QUERY && other.id !== schemaId)
      if (collision) {
        return { error: `Read model "${sanitizedName}" already exists` }
      }
      if (claimedNewNames.has(sanitizedName)) {
        return { error: `Read model "${sanitizedName}" is set by more than one entry in the batch` }
      }
      claimedNewNames.add(sanitizedName)
      updatedSchema.name = sanitizedName
      changes.push(`renamed to "${sanitizedName}"`)
      pendingRenames.push({ schemaId, newName: sanitizedName })
    }

    if (description !== undefined) {
      const normalized = normalizeDescription(description, MAX_DESCRIPTION_LENGTH)
      if (normalized.error) {
        return { error: normalized.error }
      }
      if (normalized.value) {
        updatedSchema.description = normalized.value
        changes.push('updated description')
      } else {
        updatedSchema.description = null
        changes.push('removed description')
      }
    }

    if (entity !== undefined) {
      updatedSchema.entityId = resolvedEntityId
      changes.push('updated entity')
    }

    if (cardinality !== undefined && cardinality !== schema.cardinality) {
      updatedSchema.cardinality = cardinality
      changes.push(`cardinality set to "${cardinality}"`)
    }

    if (changes.length === 0) {
      return { error: `No changes specified for "${schema.name}"` }
    }

    schemaInputs.push({
      ...updatedSchema,
      index: schemaIndex,
      expectedVersion: schema.version || null
    })
    changeSummaries.push(`"${schema.name}": ${changes.join(', ')}`)
    refsOut.push({
      name: updatedSchema.name,
      ref: buildSchemaRef('queries', updatedSchema.name)
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: schemaInputs,
    updateNote: {
      action: 'updated read models',
      target: schemaInputs.map((schema) => schema.name).join(', ')
    }
  }

  // Renaming a read model also renames every linked Read Model card (a query can be reused across events).
  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  let cardInput = null

  if (pendingRenames.length > 0) {
    const cardsToUpdate = []

    for (const { schemaId, newName } of pendingRenames) {
      const matches = findAllReadModelCardsBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)
      for (const match of matches) {
        cardsToUpdate.push({
          cardId: match.card.id,
          cardIndex: match.cardIndex,
          expectedVersion: match.card.version,
          description: newName,
          sortkey: match.card.sortkey,
          cardTypeId: match.card.cardTypeId,
          done: match.card.done ?? false,
          schemaId: match.card.schemaId,
          boundedContext: match.card.boundedContext,
          releaseNo: match.card.releaseNo,
          eventId: match.event.id,
          eventIndex: match.eventIndex
        })
      }
    }

    if (cardsToUpdate.length > 0) {
      cardInput = {
        workflowId,
        updateNote: {
          action: 'renamed read model cards',
          target: pendingRenames.map((rename) => rename.newName).join(', ')
        },
        cards: cardsToUpdate
      }
    }
  }

  return { schemaInput, cardInput, changeSummaries, refs: refsOut }
}

export const buildDeleteReadModelMutation = ({ workflow, workflowId, readModel, cardTypes }) => {
  if (!readModel) {
    return { error: 'Read model ref is required' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const resolved = resolveSchema(readModel, workflow, cardTypesMap)
  if (!resolved) {
    return { error: `Read model not found for ref: ${readModel}` }
  }
  const schemaId = resolved.id

  const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
  if (schemaIndex === -1) {
    return { error: 'Read model not found' }
  }

  const schema = workflow.schemas[schemaIndex]
  const matches = findAllReadModelCardsBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)

  // Every linked Read Model card is deleted first (a query can be reused across events);
  // each card delete and the schema delete are version-threaded by the caller.
  const cardDeletes = matches.map((match) => ({
    workflowId,
    eventId: match.event.id,
    eventIndex: match.eventIndex,
    cardId: match.card.id,
    cardIndex: match.cardIndex,
    cardDescription: match.card.description
  }))

  const schemaInput = {
    id: workflowId,
    deletedSchemas: [{ deleteIndex: schemaIndex, deleteId: schemaId }],
    updateNote: {
      action: 'deleted read model',
      target: schema.name
    }
  }

  return { cardDeletes, schemaInput, schema }
}
